import { useEffect, useRef } from 'react';
import { getChuruHitbox, getObstacleHitbox, getPlayerHitbox } from './game/collision';
import {
  CAT_RUNNING_CANVAS_HEIGHT,
  CAT_RUNNING_CANVAS_WIDTH,
  CAT_RUNNING_PLAYER_DEATH_HEIGHT,
  CAT_RUNNING_PLAYER_DEATH_WIDTH,
  CAT_RUNNING_SKY_BOTTOM_COLOR,
  CAT_RUNNING_SKY_TOP_COLOR,
} from './constants';

const drawScrollingBackground = (ctx, image, offset) => {
  const imageWidth = image?.naturalWidth || image?.width || 0;
  const imageHeight = image?.naturalHeight || image?.height || 0;

  if (!image || !imageWidth || !imageHeight) return false;

  const scale = Math.max(CAT_RUNNING_CANVAS_WIDTH / imageWidth, CAT_RUNNING_CANVAS_HEIGHT / imageHeight);
  const drawWidth = imageWidth * scale;
  const drawHeight = imageHeight * scale;
  const normalizedOffset = offset % drawWidth;
  const startX = -normalizedOffset;
  const overlapWidth = 1;

  for (let index = -1; index <= 1; index += 1) {
    ctx.drawImage(image, startX + drawWidth * index, 0, drawWidth + overlapWidth, drawHeight);
  }

  return true;
};

const drawFallbackSky = (ctx) => {
  const gradient = ctx.createLinearGradient(0, 0, 0, CAT_RUNNING_CANVAS_HEIGHT);
  gradient.addColorStop(0, CAT_RUNNING_SKY_TOP_COLOR);
  gradient.addColorStop(1, CAT_RUNNING_SKY_BOTTOM_COLOR);
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, CAT_RUNNING_CANVAS_WIDTH, CAT_RUNNING_CANVAS_HEIGHT);
};

const interpolate = (from, to, alpha) => from + (to - from) * alpha;
const getChuruFloatOffset = (floatTime) => Math.sin(floatTime) * 5;
const DEBUG_FPS_OVERLAY_ENABLED = false;
const DEBUG_HITBOXES_ENABLED = false;

const drawDebugFps = (ctx, fps, frameTimeMs) => {
  ctx.save();
  ctx.fillStyle = 'rgba(16, 33, 20, 0.7)';
  ctx.beginPath();
  ctx.roundRect(CAT_RUNNING_CANVAS_WIDTH - 112, 12, 100, 46, 10);
  ctx.fill();
  ctx.fillStyle = '#FFFFFF';
  ctx.font = "bold 14px Pretendard";
  ctx.textAlign = 'right';
  ctx.textBaseline = 'top';
  ctx.fillText(`FPS ${fps}`, CAT_RUNNING_CANVAS_WIDTH - 20, 19);
  ctx.font = "12px Pretendard";
  ctx.fillText(`${frameTimeMs.toFixed(1)} ms`, CAT_RUNNING_CANVAS_WIDTH - 20, 36);
  ctx.restore();
};

const drawObstacles = (ctx, world, assetsRef, alpha) => {
  const obstacleFrames = assetsRef.current.groundObstacles ?? [];
  const crowFrames = assetsRef.current.crowFrames ?? [];

  world.obstacles.forEach((obstacle) => {
    const obstacleImage =
      obstacle.kind === 'crow'
        ? crowFrames[obstacle.frameIndex ?? 0]?.image
        : obstacleFrames[obstacle.variantIndex]?.image;
    if (!obstacleImage) return;

    const renderX = interpolate(obstacle.previousX, obstacle.x, alpha);

    ctx.drawImage(obstacleImage, renderX, obstacle.y - obstacle.height, obstacle.width, obstacle.height);
  });
};

const drawChurus = (ctx, world, assetsRef, alpha) => {
  const churuImage = assetsRef.current.churu?.image;
  if (!churuImage) return;

  world.churus.forEach((churu) => {
    const renderX = interpolate(churu.previousX, churu.x, alpha);
    const renderY = interpolate(churu.previousY ?? churu.y, churu.y, alpha);
    const renderFloatTime = interpolate(churu.previousFloatTime ?? churu.floatTime, churu.floatTime, alpha);
    const floatOffsetY = getChuruFloatOffset(renderFloatTime);
    const centerX = renderX + churu.width / 2;
    const centerY = renderY - churu.height / 2 + floatOffsetY;
    const rotation = Math.PI / 13;

    ctx.save();
    ctx.translate(centerX, centerY);
    ctx.rotate(rotation);
    ctx.drawImage(churuImage, -churu.width / 2, -churu.height / 2, churu.width, churu.height);
    ctx.restore();
  });
};

const drawPlayer = (ctx, world, assetsRef, alpha) => {
  const runFrames = assetsRef.current.characterRun ?? [];
  const isGameOver = world.status === 'gameover';
  const currentFrame = isGameOver ? assetsRef.current.characterDeath?.image : runFrames[world.player.frameIndex]?.image;

  if (!currentFrame) return;

  // 무적(이어하기 그레이스 / 방패) 중에는 10Hz 깜빡임으로 표시 (시뮬과 동일)
  const isInvincible = !isGameOver && ((world.reviveGraceMs ?? 0) > 0 || (world.invincibleMs ?? 0) > 0);
  if (isInvincible && Math.floor(world.elapsedMs / 100) % 2 === 0) return;

  const renderY = interpolate(world.player.previousY, world.player.y, alpha);
  const drawWidth = isGameOver ? CAT_RUNNING_PLAYER_DEATH_WIDTH : world.player.width;
  const drawHeight = isGameOver ? CAT_RUNNING_PLAYER_DEATH_HEIGHT : world.player.height;

  ctx.drawImage(currentFrame, world.player.x, renderY - drawHeight, drawWidth, drawHeight);
};

const drawHitboxes = (ctx, world, alpha) => {
  const playerRenderY = interpolate(world.player.previousY, world.player.y, alpha);
  const playerHitbox = getPlayerHitbox({
    ...world.player,
    y: playerRenderY,
  });

  ctx.save();
  ctx.strokeStyle = '#FF3B30';
  ctx.lineWidth = 2;

  ctx.strokeRect(playerHitbox.x, playerHitbox.y, playerHitbox.width, playerHitbox.height);

  world.obstacles.forEach((obstacle) => {
    const obstacleHitbox = getObstacleHitbox({
      ...obstacle,
      x: interpolate(obstacle.previousX, obstacle.x, alpha),
    });

    ctx.strokeRect(obstacleHitbox.x, obstacleHitbox.y, obstacleHitbox.width, obstacleHitbox.height);
  });

  world.churus.forEach((churu) => {
    const renderFloatTime = interpolate(churu.previousFloatTime ?? churu.floatTime, churu.floatTime, alpha);
    const churuHitbox = getChuruHitbox({
      ...churu,
      x: interpolate(churu.previousX, churu.x, alpha),
      y: interpolate(churu.previousY ?? churu.y, churu.y, alpha) + getChuruFloatOffset(renderFloatTime),
    });

    ctx.strokeRect(churuHitbox.x, churuHitbox.y, churuHitbox.width, churuHitbox.height);
  });

  ctx.restore();
};

export default function CatRunningCanvas({ worldRef, assetsRef, accumulatorRef, onJump }) {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const context = canvas.getContext('2d');
    if (!context) return;

    let animationFrameId = 0;
    const devicePixelRatio = window.devicePixelRatio || 1;
    let lastDrawTimestamp = 0;
    let frameSampleElapsedMs = 0;
    let frameSampleCount = 0;
    let debugFps = 60;
    let debugFrameTimeMs = 16.7;

    const resizeCanvas = () => {
      canvas.width = Math.round(CAT_RUNNING_CANVAS_WIDTH * devicePixelRatio);
      canvas.height = Math.round(CAT_RUNNING_CANVAS_HEIGHT * devicePixelRatio);
      context.setTransform(devicePixelRatio, 0, 0, devicePixelRatio, 0, 0);
      context.imageSmoothingEnabled = true;
    };

    resizeCanvas();

    const draw = (timestamp) => {
      if (lastDrawTimestamp) {
        const frameDeltaMs = timestamp - lastDrawTimestamp;
        frameSampleElapsedMs += frameDeltaMs;
        frameSampleCount += 1;

        if (frameSampleElapsedMs >= 250) {
          debugFps = Math.max(1, Math.round((frameSampleCount * 1000) / frameSampleElapsedMs));
          debugFrameTimeMs = frameSampleElapsedMs / Math.max(frameSampleCount, 1);
          frameSampleElapsedMs = 0;
          frameSampleCount = 0;
        }
      }
      lastDrawTimestamp = timestamp;

      context.clearRect(0, 0, CAT_RUNNING_CANVAS_WIDTH, CAT_RUNNING_CANVAS_HEIGHT);

      const world = worldRef.current;
      const background = assetsRef.current.background?.image;
      const alpha = Math.min(accumulatorRef.current / (1 / 60), 1);
      const renderBackgroundOffset = interpolate(world.previousBackgroundOffset, world.backgroundOffset, alpha);

      drawFallbackSky(context);

      const didDrawBackground = drawScrollingBackground(context, background, renderBackgroundOffset);
      if (!didDrawBackground) {
        context.fillStyle = 'rgba(255, 255, 255, 0.16)';
        context.fillRect(0, 0, CAT_RUNNING_CANVAS_WIDTH, CAT_RUNNING_CANVAS_HEIGHT);
      }

      drawObstacles(context, world, assetsRef, alpha);
      drawChurus(context, world, assetsRef, alpha);
      drawPlayer(context, world, assetsRef, alpha);
      // FPS 디버그 박스는 일반 게임 화면에서 숨김 처리.
      if (DEBUG_FPS_OVERLAY_ENABLED) {
        drawDebugFps(context, debugFps, debugFrameTimeMs);
      }
      if (DEBUG_HITBOXES_ENABLED) {
        drawHitboxes(context, world, alpha);
      }
      animationFrameId = window.requestAnimationFrame(draw);
    };

    animationFrameId = window.requestAnimationFrame(draw);

    return () => {
      window.cancelAnimationFrame(animationFrameId);
      lastDrawTimestamp = 0;
    };
  }, [accumulatorRef, assetsRef, worldRef]);

  return (
    <canvas
      ref={canvasRef}
      width={CAT_RUNNING_CANVAS_WIDTH}
      height={CAT_RUNNING_CANVAS_HEIGHT}
      className="block h-auto w-full"
      style={{ aspectRatio: `${CAT_RUNNING_CANVAS_WIDTH} / ${CAT_RUNNING_CANVAS_HEIGHT}` }}
      onPointerDown={onJump}
    />
  );
}
